import React, { useState, useEffect } from 'react';
import { Button, Col, Row, Input, Select, Table, Card, Pagination, TableColumnsType, Modal, Form, message, DatePicker, notification } from 'antd';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import { appConfig } from '../config/appConfig';
import dayjs from 'dayjs';
import ReCAPTCHA from 'react-google-recaptcha';

const { Option } = Select;



// #region enums
// Başvuru Tipi enum'u
enum ApplicationTypeEnum {
    ArastirmaProjesi = 1070001,
    HibeProjesi = 1070002,
    Erasmus = 1070003,
    TematikProje = 1070004,
    Yarisma = 1070005,
    Kongre = 1070006,
    Sempozyum = 1070007,
    Webinar = 1070008,
    MakabeKabulu = 1070009,
    KitapBolumuDaveti = 1070010,
    EgitimSeminer = 1070011,
    Burs = 1070012,
    Konferans = 1070013,
    Calistay = 1070014,
    BildiriKabulu = 1070015,
}

// Enum değerlerini label ile eşle
const ApplicationTypeLabels: Record<ApplicationTypeEnum, string> = {
    [ApplicationTypeEnum.ArastirmaProjesi]: "Araştırma Projesi",
    [ApplicationTypeEnum.HibeProjesi]: "Hibe Projesi",
    [ApplicationTypeEnum.Erasmus]: "Erasmus+",
    [ApplicationTypeEnum.TematikProje]: "Tematik Proje",
    [ApplicationTypeEnum.Yarisma]: "Yarışma",
    [ApplicationTypeEnum.Kongre]: "Kongre",
    [ApplicationTypeEnum.Sempozyum]: "Sempozyum",
    [ApplicationTypeEnum.Webinar]: "Webinar",
    [ApplicationTypeEnum.MakabeKabulu]: "Makabe Kabulü",
    [ApplicationTypeEnum.KitapBolumuDaveti]: "Kitap Bölümü Daveti",
    [ApplicationTypeEnum.EgitimSeminer]: "Eğitim / Seminer",
    [ApplicationTypeEnum.Burs]: "Burs",
    [ApplicationTypeEnum.Konferans]: "Konferans",
    [ApplicationTypeEnum.Calistay]: "Çalıştay",
    [ApplicationTypeEnum.BildiriKabulu]: "Bildiri Kabulü"
}

// Alan Türü enum'u (numeric key)
enum FieldTypeEnum {
    FenBilimleri = 1071001,
    SosyalBilimler = 1071002,
    SaglikBilimleri = 1071003,
    Genel = 1071004,
    YasamBilimleri = 1071005,
    UygulamaliBilimler = 1071006,
}
const FieldTypeLabels: Record<FieldTypeEnum, string> = {
    [FieldTypeEnum.FenBilimleri]: "Fen Bilimleri",
    [FieldTypeEnum.SosyalBilimler]: "Sosyal Bilimler",
    [FieldTypeEnum.SaglikBilimleri]: "Sağlık Bilimleri",
    [FieldTypeEnum.Genel]: "Genel",
    [FieldTypeEnum.YasamBilimleri]: "Yaşam Bilimleri",
    [FieldTypeEnum.UygulamaliBilimler]: "Uygulamalı Bilimler"
}

// Kapsam enum'u (numeric key)
enum ScopeInformationEnum {
    Ulusal = 1072001,
    Uluslararasi = 1072002
}
const ScopeInformationLabels: Record<ScopeInformationEnum, string> = {
    [ScopeInformationEnum.Ulusal]: "Ulusal",
    [ScopeInformationEnum.Uluslararasi]: "Uluslararası"
}

// Yürütücü Kurum enum'u (numeric key, unique keys)
enum ExecutingInstitutionEnum {
    Diger = 1073001,
    Tubitak = 1073002,
    UlusalAjans = 1073003,
    AvrupaBirligiBaskanligi = 1073004,
    IKG = 1073005,
    OKA = 1073006,
    KOSGEB = 1073007,
    AnkaraUniversitesi = 1073008,
    MuhtelifUniversiteler = 1073009,
    TEKNOFEST = 1073010,
    AFAD = 1073011,
    TarimOrmanBakanligi = 1073012,
    TUSEB = 1073013,
    YuksekogretimKurulu = 1073014,
}
const ExecutingInstitutionLabels: Record<ExecutingInstitutionEnum, string> = {
    [ExecutingInstitutionEnum.Diger]: "Diğer",
    [ExecutingInstitutionEnum.Tubitak]: "Tübitak",
    [ExecutingInstitutionEnum.UlusalAjans]: "Ulusal Ajans",
    [ExecutingInstitutionEnum.AvrupaBirligiBaskanligi]: "Avrupa Birliği Başkanlığı",
    [ExecutingInstitutionEnum.IKG]: "IKG",
    [ExecutingInstitutionEnum.OKA]: "OKA",
    [ExecutingInstitutionEnum.KOSGEB]: "KOSGEB",
    [ExecutingInstitutionEnum.AnkaraUniversitesi]: "Ankara Üniversitesi",
    [ExecutingInstitutionEnum.MuhtelifUniversiteler]: "Muhtelif Üniversiteler",
    [ExecutingInstitutionEnum.TEKNOFEST]: "TEKNOFEST",
    [ExecutingInstitutionEnum.AFAD]: "AFAD",
    [ExecutingInstitutionEnum.TarimOrmanBakanligi]: "Tarım ve Orman Bakanlığı",
    [ExecutingInstitutionEnum.TUSEB]: "TÜSEB",
    [ExecutingInstitutionEnum.YuksekogretimKurulu]: "Yükseköğretim Kurulu Başkanlığı"
}

// Kimler Başvurabilir enum'u (numeric key)
enum WhoCanApplyEnum {
    Akademisyen = 1074001,
    Ogrenci = 1074002,
    Kurumsal = 1074003,
    Genel = 1074004,
    Arastirmaci = 1074005,
}
const WhoCanApplyLabels: Record<WhoCanApplyEnum, string> = {
    [WhoCanApplyEnum.Akademisyen]: "Akademisyen",
    [WhoCanApplyEnum.Ogrenci]: "Öğrenci",
    [WhoCanApplyEnum.Kurumsal]: "Kurumsal",
    [WhoCanApplyEnum.Genel]: "Genel",
    [WhoCanApplyEnum.Arastirmaci]: "Araştırmacı",
}
// #endregion enums
interface DataType {
    key: React.Key;
    applicationTypeId: number;
    fieldTypeId: number;
    scopeId: number;
    institutionId: number;
    whoCanApplyId: number;
    // Ekranda gösterilecek alanlar
    title: string;
    applicationTypeName: string;
    fieldTypeName: string;
    scopeName: string;
    institutionName: string;
    whoCanApplyName: string; startDate: string;
    endDate: string;
    dateStatusName: string;
    urlText: string;
    filePath: string;
}
const columns: TableColumnsType<DataType> = [
    {
        title: 'Başlık',
        dataIndex: 'title',
        key: 'title',
        showSorterTooltip: { target: 'full-header' },
        sorter: (a: any, b: any) => a.title.localeCompare(b.title, 'tr'),
    },
    {
        title: 'Başvuru Tipi',
        dataIndex: 'applicationTypeName',
        key: 'applicationType',
        showSorterTooltip: { target: 'full-header' },
        sorter: (a: any, b: any) => 0,
    },
    {
        title: 'Alan Türü',
        dataIndex: 'fieldTypeName',
        key: 'fieldType',
        showSorterTooltip: { target: 'full-header' },
        sorter: (a: any, b: any) => 0,
    },
    {
        title: 'Kapsam',
        dataIndex: 'scopeName',
        key: 'scope',
        sorter: (a: any, b: any) => 0,
    },
    {
        title: 'Yürütücü Kurum',
        dataIndex: 'institutionName',
        key: 'institution',
        sorter: (a: any, b: any) => 0,
    },
    {
        title: 'Kimler Başvurabilir',
        dataIndex: 'whoCanApplyName',
        key: 'whoCanApply',
        sorter: (a: any, b: any) => 0,
    },
    {
        title: 'Başlangıç Tarihi',
        dataIndex: 'startDate',
        key: 'startDate',
        sorter: (a: any, b: any) => 0,
        render: (value: any, record: any, index: any) => (
            <>
                {dayjs(value).format('DD.MM.YYYY')}
            </>
        )
    },
    {
        title: 'Bitiş Tarihi',
        dataIndex: 'endDate',
        key: 'endDate',
        sorter: (a: any, b: any) => 0,
        render: (value: any, record: any, index: any) => (
            <>
                {dayjs(value).format('DD.MM.YYYY')}
            </>
        )
    },
    {
        title: 'Durum',
        dataIndex: 'dateStatusName',
        key: 'dateStatusName',
    },
    {
        title: 'Bağlantı',
        dataIndex: 'urlText',
        key: 'urlText',
        render: (value: string) => (
            value ? (
                <a href={value} target="_blank" rel="noopener noreferrer">
                    Detay
                </a>
            ) : null
        ),
    },
    {
        title: 'Dosya',
        dataIndex: 'filePath',
        key: 'filePath',
        render: (value: string) => (
            value ? (
                <a href={value} target="_blank" rel="noopener noreferrer">
                    <DownloadOutlined alt='document' about='Doküman' />
                </a>
            ) : null
        ),
    },
] as any;


const AnnouncementsSection = () => {
    // State tanımlamaları
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [dataCount, setDataCount] = useState(15);
    const [totalDataCount, setTotalDataCount] = useState(data?.length || 0);
    const [filter, setFilter] = useState<any>({});
    const [sortedInfo, setSortedInfo] = useState<any>({});
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const [messageApi, messageContextHolder] = message.useMessage();
    const [api, notificationContextHolder] = notification.useNotification();

    // Modal açıldığında captcha üret
    useEffect(() => {
        if (isModalVisible) {
            form.resetFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalVisible]);

    // Sayfa ilk render olduğunda çalışacak method
    useEffect(() => {
        fetchAnnouncements(filter, sortedInfo, page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, sortedInfo, page]);

    // Filtre değiştikçe çağrılacak fonksiyon
    const handleFilterChange = (field: string, value: any) => {
        const newFilter = { ...filter, [field]: value };
        setFilter(newFilter);
    };

    // Sayfa değiştikçe çağrılacak fonksiyon
    const changeGridPage = (newPage: number) => {
        setPage(newPage);
    };

    // Backend'den veri çekme fonksiyonu
    const fetchAnnouncements = async (filterObj: any, sortObj: any, pageNum: number) => {
        try {
            // Örnek endpoint ve parametreler
            var filters = [] as any;
            if (filterObj.title) {
                filters.push({
                    customFilter: false,
                    field: "title",
                    operator: "Contains",
                    type: "input",
                    value: filterObj.title,
                })
            }
            if (filterObj.applicationTypeId) {
                filters.push({
                    customFilter: false,
                    field: "applicationTypeId",
                    operator: "=",
                    type: "select",
                    value: filterObj.applicationTypeId,
                })
            }
            if (filterObj.fieldTypeId) {
                filters.push({
                    customFilter: false,
                    field: "fieldTypeId",
                    operator: "=",
                    type: "select",
                    value: filterObj.fieldTypeId,
                })
            }
            if (filterObj.scopeId) {
                filters.push({
                    customFilter: false,
                    field: "scopeId",
                    operator: "=",
                    type: "select",
                    value: filterObj.scopeId,
                })
            }
            if (filterObj.institutionId) {
                filters.push({
                    customFilter: false,
                    field: "institutionId",
                    operator: "=",
                    type: "select",
                    value: filterObj.institutionId,
                })
            }
            if (filterObj.whoCanApplyId) {
                filters.push({
                    customFilter: false,
                    field: "whoCanApplyId",
                    operator: "=",
                    type: "select",
                    value: filterObj.whoCanApplyId,
                })
            }
            const response = await axios.post(appConfig.api.endPoint + '/api/Ardek/GetArdekAnnouncementList', {
                Filters: filters,
                PageIndex: pageNum,
                PageSize: 15,
                Sorts: sortObj?.columnKey ? [{
                    CustomSort: false,
                    Direction: sortObj.order == "descend" ? "desc" : "asc",
                    Field: (sortObj.columnKey == "applicationType" ||
                        sortObj.columnKey == "fieldType" ||
                        sortObj.columnKey == "scope" ||
                        sortObj.columnKey == "institution" ||
                        sortObj.columnKey == "whoCanApply"
                    ) ? (sortObj.columnKey + ".Description") : sortObj.columnKey,
                }] : []
            });
            var result = response?.data?.result;
            if (result && !result.error) {
                setData(result.listDto || []);
                setTotalDataCount(result.totalCount || 0);
            } else {
                setData([]);
                setTotalDataCount(0);
            }
            // setData(response.data.items || []);
            // setTotalDataCount(response.data.totalCount || 0);
        } catch (error) {
            // Hata yönetimi
            setData([]);
            setTotalDataCount(0);
        }
    };
    const handleChange = (pagination: any, filters: any, sorter: any) => {
        setSortedInfo(sorter);
    };
    const renderFooter = (pageData: any) => {
        var startCount = (page - 1) != 0 ? ((page - 1) * dataCount) : 1;

        return (<Row>
            <Col className="alignR footer-text" span={24}>
                {totalDataCount} kayıttan {startCount}-{((startCount != 1 ? startCount : 0) + dataCount) > totalDataCount ? totalDataCount : ((startCount != 1 ? startCount : 0) + dataCount)} arası kayıtlar gösteriliyor.
            </Col>
            <Col className="alignR" style={{ display: "flex", justifyContent: "flex-end" }} span={24}>
                <Pagination
                    current={page || 1}
                    hideOnSinglePage
                    defaultPageSize={dataCount || 15}
                    pageSizeOptions={['15']}
                    onChange={changeGridPage}
                    total={totalDataCount}
                />
            </Col>
        </Row>
        );
    }
    // Modal aç/kapat fonksiyonları
    const showModal = () => setIsModalVisible(true);
    const handleModalCancel = () => setIsModalVisible(false);

    // Form submit
    const handleSuggest = async (values: any) => {
        if (!recaptchaToken) {
            messageApi.error('Lütfen güvenlik doğrulamasını tamamlayınız.');
            return;
        }
        try {
            const response = await axios.post(appConfig.api.endPoint + '/api/Ardek/AddArdekAnnouncement', {
                title: values.title,
                applicationTypeId: values.applicationTypeId,
                fieldTypeId: values.fieldTypeId,
                scopeId: values.scopeId,
                institutionId: values.institutionId,
                startDate: values.startDate,
                endDate: values.endDate,
                urlText: values.urlText,
                recaptchaToken: recaptchaToken,
            });
            var result = response?.data?.result;
            if (result && !result.error) {
                messageApi.success('İlan öneriniz başarıyla gönderildi!');
                setIsModalVisible(false);
                form.resetFields();
                setRecaptchaToken(null);
            } else {
                messageApi.error('Bir hata oluştu!');
            }
        } catch (err) {
            messageApi.error('Bir hata oluştu!');
        }
    };

    // Modal açıldığında reCAPTCHA sıfırla
    useEffect(() => {
        if (isModalVisible) {
            setRecaptchaToken(null);
            form.resetFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalVisible]);

    return (
        <>
            {messageContextHolder}
            {notificationContextHolder}
            <div className="ankara-announcements-header">
                <h2>İlanlar</h2>
                <div className="suggest-button">
                    <span>Katkıda Bulunmak İster misiniz?</span>
                    <Button
                        type="primary"
                        icon={<PlusOutlined color={"white"} style={{ color: 'white' }} />}
                        style={{ backgroundColor: '#ff6600', borderColor: '#ff6600' }}
                        onClick={showModal}
                    >
                        <span style={{ color: 'white' }}>İlan Öner</span>
                    </Button>
                </div>
            </div>
            {/* Modal Başlangıcı */}
            <Modal
                title="İlan Öner"
                open={isModalVisible}
                onCancel={handleModalCancel}
                width={800}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSuggest}
                >
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="İlan Başlığı"
                                name="title"
                                rules={[{ required: true, message: 'Başlık giriniz' }]}
                            >
                                <Input placeholder="Lütfen ilana ait başlığı girin" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Başvuru Tipi"
                                name="applicationTypeId"
                                rules={[{ required: true, message: 'Başvuru Tipi seçiniz' }]}
                            >
                                <Select placeholder="Lütfen Başvuru Tipi seçin" allowClear>
                                    {Object.entries(ApplicationTypeLabels).map(([key, value]) => (
                                        <Option key={key} value={Number(key)}>{value}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Alan Türü"
                                name="fieldTypeId"
                                rules={[{ required: true, message: 'Alan Türü seçiniz' }]}
                            >
                                <Select placeholder="Lütfen Alan türünü seçin" allowClear>
                                    {Object.entries(FieldTypeLabels).map(([key, value]) => (
                                        <Option key={key} value={Number(key)}>{value}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Kapsam"
                                name="scopeId"
                                rules={[{ required: true, message: 'Kapsam seçiniz' }]}
                            >
                                <Select placeholder="Lütfen kapsam bilgisi seçin" allowClear>
                                    {Object.entries(ScopeInformationLabels).map(([key, value]) => (
                                        <Option key={key} value={Number(key)}>{value}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Yürütücü Kurum"
                                name="institutionId"
                                rules={[{ required: true, message: 'Yürütücü Kurum seçiniz' }]}
                            >
                                <Select placeholder="Lütfen Yürütücü Kurum seçin" allowClear>
                                    {Object.entries(ExecutingInstitutionLabels).map(([key, value]) => (
                                        <Option key={key} value={Number(key)}>{value}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Başlangıç Tarihi"
                                name="startDate"
                                rules={[{ required: true, message: 'Başlangıç tarihi giriniz' }]}
                            >
                                <DatePicker
                                    format="DD.MM.YYYY"
                                    style={{ width: '100%' }}
                                    placeholder="gg.aa.yyyy"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Bitiş Tarihi"
                                name="endDate"
                                rules={[{ required: true, message: 'Bitiş tarihi giriniz' }]}
                            >
                                <DatePicker
                                    format="DD.MM.YYYY"
                                    style={{ width: '100%' }}
                                    placeholder="gg.aa.yyyy"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                label="Link"
                                name="urlText"
                                rules={[
                                    { required: true, message: 'Link giriniz' },
                                    { type: 'url', message: 'Geçerli bir link giriniz (örn: https://... )' }
                                ]}
                            >
                                <Input placeholder="https://..." />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item shouldUpdate>
                                <ReCAPTCHA
                                    sitekey={appConfig.captchaSiteKey}
                                    onChange={(token: any) => setRecaptchaToken(token)}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" block>
                                    Kaydet
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
            {/* Modal Sonu */}
            <Card>
                <Row gutter={[16, 16]} className="ankara-filter-row">
                    <Col xs={24} md={8} lg={4}>
                        <label className='filter-label' htmlFor="filter-title">İlan Başlığı</label>
                        <Input
                            id="filter-title"
                            placeholder="Lütfen ilana ait başlığı girin"
                            onBlurCapture={e => handleFilterChange('title', e.target.value)}
                        />
                    </Col>
                    <Col xs={24} md={8} lg={4}>
                        <label className='filter-label' htmlFor="filter-application-type">Başvuru Tipi</label>
                        <Select
                            id="filter-application-type"
                            placeholder="Lütfen Başvuru Tipi seçin"
                            allowClear
                            style={{ width: '100%' }}
                            onChange={value => handleFilterChange('applicationTypeId', value)}
                        >
                            {Object.entries(ApplicationTypeLabels).map(([key, value]) => (
                                <Option key={key} value={Number(key)}>{value}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={8} lg={4}>
                        <label className='filter-label' htmlFor="filter-field-type">Alan Türü</label>
                        <Select
                            id="filter-field-type"
                            placeholder="Lütfen Alan türünü seçin"
                            allowClear
                            style={{ width: '100%' }}
                            onChange={value => handleFilterChange('fieldTypeId', value)}
                        >
                            {Object.entries(FieldTypeLabels).map(([key, value]) => (
                                <Option key={key} value={Number(key)}>{value}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={8} lg={4}>
                        <label className='filter-label' htmlFor="filter-scope">Kapsam</label>
                        <Select
                            id="filter-scope"
                            placeholder="Lütfen kapsam bilgisi seçin"
                            allowClear
                            style={{ width: '100%' }}
                            onChange={value => handleFilterChange('scopeId', value)}
                        >
                            {Object.entries(ScopeInformationLabels).map(([key, value]) => (
                                <Option key={key} value={Number(key)}>{value}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={8} lg={4}>
                        <label className='filter-label' htmlFor="filter-institution">Yürütücü Kurum</label>
                        <Select
                            id="filter-institution"
                            placeholder="Lütfen Yürütücü Kurum seçin"
                            allowClear
                            style={{ width: '100%' }}
                            onChange={value => handleFilterChange('institutionId', value)}
                        >
                            {Object.entries(ExecutingInstitutionLabels).map(([key, value]) => (
                                <Option key={key} value={Number(key)}>{value}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} md={8} lg={4}>
                        <label className='filter-label' htmlFor="filter-who-can-apply">Kimler Başvurabilir</label>
                        <Select
                            id="filter-who-can-apply"
                            placeholder="Lütfen Başvuru Tipi seçin"
                            allowClear
                            style={{ width: '100%' }}
                            onChange={value => handleFilterChange('whoCanApplyId', value)}
                        >
                            {Object.entries(WhoCanApplyLabels).map(([key, value]) => (
                                <Option key={key} value={Number(key)}>{value}</Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
            </Card>
            <Table
                columns={columns}
                dataSource={data}
                pagination={false}
                footer={renderFooter}
                locale={{
                    emptyText: 'Kayıt bulunamadı',
                    sortTitle: 'Sıralamak için tıklayın',
                    triggerDesc: 'Sıralamak için tıklayın',
                    triggerAsc: 'Sıralamak için tıklayın',
                    cancelSort: 'Sıralamayı iptal et'
                }}
                showSorterTooltip={{ target: 'sorter-icon' }}
                onChange={handleChange}

                // sticky
                scroll={{ x: 'max-content' }}
            // loading={false}
            // size="small"
            />
        </>
    );
};

export default AnnouncementsSection;